/**
 * Activity Page — API call log with BigQuery metadata.
 *
 * Uses Firestore real-time listeners so new API calls appear instantly
 * without polling. Entries and stats are streamed via onSnapshot.
 */
import { useState, useCallback, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  collection,
  doc,
  orderBy,
  limit,
  query,
  onSnapshot,
  type DocumentData,
} from 'firebase/firestore';
import {
  Activity,
  Database,
  DollarSign,
  HardDrive,
  Trash2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  Check,
  Search,
  X,
  Clock,
  Zap,
  User,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { TopBar } from '@/components/layout/TopBar';
import { EmptyState } from '@/components/ui/EmptyState';
import { useAuth } from '@/contexts/AuthContext';
import { getFirebaseFirestore } from '@/lib/firebase';
import { api } from '@/services/api';
import { endpoints } from '@/services/endpoints';
import { toast } from '@/components/ui/Toast';

// ─── Types ──────────────────────────────────────────────────────────────────

interface BigQueryStats {
  query: string;
  durationMs: number;
  totalBytesProcessed: number;
  totalBytesBilled: number;
  cacheHit: boolean;
  costUsd: number;
}

interface ActivityEntry {
  id: string;
  timestamp: string;
  method: string;
  url: string;
  section: string;
  statusCode: number;
  durationMs: number;
  requestBody?: unknown;
  responseSize?: number;
  bigquery?: BigQueryStats[];
  /** Account that made the call. Absent on entries logged before attribution. */
  user?: string;
}

interface ActivityStats {
  totalCalls: number;
  totalBigQueryQueries: number;
  totalBytesProcessed: number;
  totalBytesBilled: number;
  totalCostUsd: number;
  callsBySection: Record<string, number>;
  callsByMethod: Record<string, number>;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const val = bytes / Math.pow(1024, i);
  return `${val < 10 ? val.toFixed(2) : val < 100 ? val.toFixed(1) : Math.round(val)} ${units[i]}`;
}

function formatCost(usd: number): string {
  if (usd === 0) return '$0.00';
  if (usd < 0.001) return '<$0.001';
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(3)}`;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/** `eddieb@applift.co` → `eddieb`; the full address stays in the tooltip. */
function shortUser(email: string): string {
  return email.split('@')[0] || email;
}

/** Filter value for entries with nobody to attribute them to. */
const UNATTRIBUTED = '\u0000none';

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

function generateCurl(entry: ActivityEntry): string {
  const base = window.location.origin;
  let cmd = `curl -X ${entry.method} '${base}/api${entry.url.replace(/^\/api/, '')}'`;
  if (entry.requestBody) {
    cmd += ` \\\n  -H 'Content-Type: application/json' \\\n  -d '${JSON.stringify(entry.requestBody)}'`;
  }
  return cmd;
}

const METHOD_COLORS: Record<string, string> = {
  GET: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  POST: 'bg-blue-50 text-blue-700 border-blue-200',
  PATCH: 'bg-amber-50 text-amber-700 border-amber-200',
  DELETE: 'bg-rose-50 text-rose-700 border-rose-200',
};

const STATUS_COLORS: Record<string, string> = {
  '2': 'bg-emerald-50 text-emerald-700',
  '3': 'bg-sky-50 text-sky-700',
  '4': 'bg-amber-50 text-amber-700',
  '5': 'bg-rose-50 text-rose-700',
};

function statusColor(code: number): string {
  return STATUS_COLORS[String(code)[0]] ?? 'bg-slate-50 text-slate-700';
}

const SECTION_LABELS: Record<string, string> = {
  product: 'Product',
  flow: 'Flow',
  marketing: 'Marketing',
  users: 'Users',
  purchases: 'Purchases',
  push_notifications: 'Push',
  reports: 'Reports',
  fields: 'Fields',
  system: 'System',
  activity: 'Activity',
};

const SECTIONS_FOR_FILTER = [
  'product', 'flow', 'marketing', 'users', 'purchases', 'push_notifications',
  'reports', 'fields', 'system',
];

const PAGE_SIZE = 50;
const MAX_LISTENER_DOCS = 500;

// ─── Firestore → ActivityEntry mapper ───────────────────────────────────────

function docToEntry(d: DocumentData): ActivityEntry {
  return {
    id: d.id,
    timestamp: d.timestamp,
    method: d.method,
    url: d.url,
    section: d.section,
    statusCode: d.statusCode,
    durationMs: d.durationMs,
    requestBody: d.requestBody,
    responseSize: d.responseSize,
    bigquery: d.bigquery,
    user: d.user,
  };
}

function docToStats(d: DocumentData): ActivityStats {
  return {
    totalCalls: d.totalCalls ?? 0,
    totalBigQueryQueries: d.totalBigQueryQueries ?? 0,
    totalBytesProcessed: d.totalBytesProcessed ?? 0,
    totalBytesBilled: d.totalBytesBilled ?? 0,
    totalCostUsd: d.totalCostUsd ?? 0,
    callsBySection: d.callsBySection ?? {},
    callsByMethod: d.callsByMethod ?? {},
  };
}

// ─── Main Component ─────────────────────────────────────────────────────────

export function ActivityPage() {
  const { firestoreRootCollection } = useAuth();
  const [allEntries, setAllEntries] = useState<ActivityEntry[]>([]);
  const [stats, setStats] = useState<ActivityStats | null>(null);
  const [entriesLoading, setEntriesLoading] = useState(true);

  const [sectionFilter, setSectionFilter] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [bqOnly, setBqOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const resetPage = useCallback(() => setPage(1), []);

  // ── Firestore real-time listener for entries ────────────────────────────
  useEffect(() => {
    const db = getFirebaseFirestore();
    const entriesRef = collection(db, firestoreRootCollection, 'activity', 'entries');
    const q = query(entriesRef, orderBy('timestamp', 'desc'), limit(MAX_LISTENER_DOCS));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const entries = snapshot.docs.map((d) => docToEntry(d.data()));
        setAllEntries(entries);
        setEntriesLoading(false);
      },
      (err) => {
        console.error('[Activity] Entries listener error:', err);
        setEntriesLoading(false);
      },
    );

    return unsubscribe;
  }, [firestoreRootCollection]);

  // ── Firestore real-time listener for stats ──────────────────────────────
  useEffect(() => {
    const db = getFirebaseFirestore();
    const statsRef = doc(db, firestoreRootCollection, 'activity', 'meta', 'stats');

    const unsubscribe = onSnapshot(
      statsRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setStats(docToStats(snapshot.data()));
        } else {
          setStats({
            totalCalls: 0,
            totalBigQueryQueries: 0,
            totalBytesProcessed: 0,
            totalBytesBilled: 0,
            totalCostUsd: 0,
            callsBySection: {},
            callsByMethod: {},
          });
        }
      },
      (err) => {
        console.error('[Activity] Stats listener error:', err);
      },
    );

    return unsubscribe;
  }, [firestoreRootCollection]);

  /** Accounts seen in the loaded entries, for the "who" dropdown. */
  const knownUsers = useMemo(() => {
    const seen = new Set<string>();
    for (const e of allEntries) if (e.user) seen.add(e.user);
    return [...seen].sort();
  }, [allEntries]);

  // ── Client-side filtering ───────────────────────────────────────────────
  const filtered = useMemo(() => {
    let results = allEntries;
    if (sectionFilter) results = results.filter((e) => e.section === sectionFilter);
    if (methodFilter) results = results.filter((e) => e.method === methodFilter);
    if (userFilter) {
      results = userFilter === UNATTRIBUTED
        ? results.filter((e) => !e.user)
        : results.filter((e) => e.user === userFilter);
    }
    if (bqOnly) results = results.filter((e) => e.bigquery && e.bigquery.length > 0);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      results = results.filter(
        (e) => e.url.toLowerCase().includes(q) || (e.user ?? '').toLowerCase().includes(q),
      );
    }
    return results;
  }, [allEntries, sectionFilter, methodFilter, userFilter, bqOnly, searchQuery]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const entries = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleClear = useCallback(async () => {
    await api.delete(endpoints.activity.clear());
    setPage(1);
    toast.success('Activity log cleared');
  }, []);

  const hasFilters = sectionFilter || methodFilter || userFilter || bqOnly || searchQuery;

  return (
    <>
      <TopBar
        title="API Activity"
        subtitle={`${stats?.totalCalls ?? 0} calls logged${hasFilters ? ` \u00b7 ${total} matching` : ''}${totalPages > 1 ? ` \u00b7 Page ${page}/${totalPages}` : ''}`}
        actions={
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1.5 text-[10px] text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Live
            </span>
            <Button
              variant="ghost"
              size="sm"
              icon={<Trash2 />}
              onClick={handleClear}
            >
              Clear
            </Button>
          </div>
        }
      />

      <div className="px-8 py-6 space-y-4 max-w-7xl">
        {/* Summary Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryCard
              label="Total Calls"
              value={String(stats.totalCalls)}
              icon={<Activity className="w-4 h-4" />}
              color="slate"
            />
            <SummaryCard
              label="BQ Queries"
              value={String(stats.totalBigQueryQueries)}
              icon={<Database className="w-4 h-4" />}
              color="indigo"
            />
            <SummaryCard
              label="Data Scanned"
              value={formatBytes(stats.totalBytesProcessed)}
              icon={<HardDrive className="w-4 h-4" />}
              color="sky"
            />
            <SummaryCard
              label="Est. Cost"
              value={formatCost(stats.totalCostUsd)}
              subtext="$6.25 / TiB"
              icon={<DollarSign className="w-4 h-4" />}
              color="amber"
            />
          </div>
        )}

        {/* Filters */}
        <Card padding="none">
          <div className="flex flex-wrap items-center gap-2 px-4 py-2.5">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); resetPage(); }}
                placeholder="Search by URL or account..."
                className="w-full h-8 pl-8 pr-3 rounded-lg border border-slate-200 bg-white text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Section filter */}
            <select
              value={sectionFilter}
              onChange={(e) => { setSectionFilter(e.target.value); resetPage(); }}
              className="h-8 px-2 rounded-lg border border-slate-200 bg-white text-xs text-slate-700"
            >
              <option value="">All sections</option>
              {SECTIONS_FOR_FILTER.map((s) => (
                <option key={s} value={s}>{SECTION_LABELS[s] ?? s}</option>
              ))}
            </select>

            {/* Method filter */}
            <select
              value={methodFilter}
              onChange={(e) => { setMethodFilter(e.target.value); resetPage(); }}
              className="h-8 px-2 rounded-lg border border-slate-200 bg-white text-xs text-slate-700"
            >
              <option value="">All methods</option>
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PATCH">PATCH</option>
              <option value="DELETE">DELETE</option>
            </select>

            {/* Who made the call — only meaningful once attributed entries exist */}
            {knownUsers.length > 0 && (
              <select
                value={userFilter}
                onChange={(e) => { setUserFilter(e.target.value); resetPage(); }}
                className="h-8 px-2 rounded-lg border border-slate-200 bg-white text-xs text-slate-700 max-w-[180px]"
              >
                <option value="">All accounts</option>
                {knownUsers.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
                <option value={UNATTRIBUTED}>Unattributed</option>
              </select>
            )}

            {/* BQ only toggle */}
            <button
              onClick={() => { setBqOnly(!bqOnly); resetPage(); }}
              className={cn(
                'h-8 px-3 rounded-lg border text-xs font-medium transition-colors',
                bqOnly
                  ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50',
              )}
            >
              <Database className="w-3 h-3 inline mr-1" />
              BQ Only
            </button>

            {hasFilters && (
              <button
                onClick={() => { setSectionFilter(''); setMethodFilter(''); setUserFilter(''); setBqOnly(false); setSearchQuery(''); resetPage(); }}
                className="h-8 px-2 text-xs text-slate-500 hover:text-slate-700"
              >
                Clear filters
              </button>
            )}
          </div>
        </Card>

        {/* Activity List */}
        {entriesLoading && entries.length === 0 ? (
          <Card>
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-12 bg-slate-100 rounded-lg animate-pulse" />
              ))}
            </div>
          </Card>
        ) : entries.length === 0 ? (
          <Card>
            <EmptyState
              icon={Activity}
              title="No activity yet"
              description={hasFilters ? 'No API calls match the current filters' : 'API calls will appear here as you use the dashboard'}
            />
          </Card>
        ) : (
          <div className="space-y-1">
            <AnimatePresence initial={false}>
              {entries.map((entry) => (
                <ActivityRow
                  key={entry.id}
                  entry={entry}
                  isExpanded={expandedId === entry.id}
                  onToggle={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                />
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">
              Showing {Math.min((page - 1) * PAGE_SIZE + 1, total)}&ndash;{Math.min(page * PAGE_SIZE, total)} of {total}
            </p>
            <div className="flex items-center gap-1">
              <button
                disabled={page <= 1}
                onClick={() => setPage(1)}
                className="h-8 px-2 rounded-lg text-xs text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                First
              </button>
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="h-8 w-8 flex items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {(() => {
                const pages: number[] = [];
                const start = Math.max(1, page - 2);
                const end = Math.min(totalPages, page + 2);
                for (let i = start; i <= end; i++) pages.push(i);
                return pages.map((p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={cn(
                      'h-8 w-8 rounded-lg text-xs font-medium transition-colors',
                      p === page
                        ? 'bg-indigo-600 text-white'
                        : 'text-slate-600 hover:bg-slate-100',
                    )}
                  >
                    {p}
                  </button>
                ));
              })()}
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="h-8 w-8 flex items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage(totalPages)}
                className="h-8 px-2 rounded-lg text-xs text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ─── Summary Card ───────────────────────────────────────────────────────────

const CARD_COLORS: Record<string, string> = {
  slate: 'bg-slate-50 text-slate-600',
  indigo: 'bg-indigo-50 text-indigo-600',
  sky: 'bg-sky-50 text-sky-600',
  amber: 'bg-amber-50 text-amber-600',
};

function SummaryCard({
  label,
  value,
  subtext,
  icon,
  color,
}: {
  label: string;
  value: string;
  subtext?: string;
  icon: React.ReactNode;
  color: string;
}) {
  return (
    <Card className="!py-3 !px-4">
      <div className="flex items-center gap-3">
        <div className={cn('w-9 h-9 rounded-xl flex items-center justify-center shrink-0', CARD_COLORS[color])}>
          {icon}
        </div>
        <div className="min-w-0">
          <p className="text-xs text-slate-500">{label}</p>
          <p className="text-lg font-bold text-slate-900 tabular-nums truncate">{value}</p>
          {subtext && <p className="text-[10px] text-slate-400">{subtext}</p>}
        </div>
      </div>
    </Card>
  );
}

// ─── Activity Row ───────────────────────────────────────────────────────────

function ActivityRow({
  entry,
  isExpanded,
  onToggle,
}: {
  entry: ActivityEntry;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const hasBQ = entry.bigquery && entry.bigquery.length > 0;
  const totalBQDuration = hasBQ
    ? entry.bigquery!.reduce((s, q) => s + q.durationMs, 0)
    : 0;
  const totalBQBytes = hasBQ
    ? entry.bigquery!.reduce((s, q) => s + q.totalBytesProcessed, 0)
    : 0;
  const totalBQCost = hasBQ
    ? entry.bigquery!.reduce((s, q) => s + q.costUsd, 0)
    : 0;

  return (
    <motion.div
      layout
      className="bg-white rounded-lg border border-slate-200/80 overflow-hidden"
    >
      {/* Compact row */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-slate-50/50 transition-colors"
      >
        {/* Expand icon */}
        <div className="shrink-0 text-slate-400">
          {isExpanded ? (
            <ChevronDown className="w-3.5 h-3.5" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5" />
          )}
        </div>

        {/* Method badge */}
        <span className={cn(
          'shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border',
          METHOD_COLORS[entry.method] ?? 'bg-slate-50 text-slate-600 border-slate-200',
        )}>
          {entry.method}
        </span>

        {/* URL */}
        <span className="flex-1 text-xs font-mono text-slate-700 truncate">
          {entry.url}
        </span>

        {/* Who made the call */}
        {entry.user && (
          <span
            title={entry.user}
            className="shrink-0 max-w-[120px] truncate px-1.5 py-0.5 rounded bg-violet-50 text-[10px] font-medium text-violet-700 border border-violet-100 hidden md:inline"
          >
            {shortUser(entry.user)}
          </span>
        )}

        {/* Section */}
        <span className="shrink-0 px-1.5 py-0.5 rounded bg-slate-100 text-[10px] font-medium text-slate-500 hidden sm:inline">
          {SECTION_LABELS[entry.section] ?? entry.section}
        </span>

        {/* Status code */}
        <span className={cn(
          'shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold tabular-nums',
          statusColor(entry.statusCode),
        )}>
          {entry.statusCode}
        </span>

        {/* BQ scanned bytes (fixed width for alignment) */}
        <span className="shrink-0 w-20 text-right">
          {hasBQ ? (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-indigo-600 px-1.5 py-0.5 rounded bg-indigo-50">
              <Database className="w-2.5 h-2.5" />
              {formatBytes(totalBQBytes)}
            </span>
          ) : null}
        </span>

        {/* Cost / price (fixed width for alignment) */}
        <span className="shrink-0 w-16 text-right text-[11px] tabular-nums font-medium">
          {hasBQ ? (
            <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-600">
              {formatCost(totalBQCost)}
            </span>
          ) : (
            <span className="text-slate-400">&mdash;</span>
          )}
        </span>

        {/* Duration (fixed width for alignment) */}
        <span className="shrink-0 text-[11px] tabular-nums text-slate-500 w-14 text-right">
          {formatDuration(entry.durationMs)}
        </span>

        {/* Timestamp */}
        <span className="shrink-0 text-[10px] text-slate-400 w-14 text-right" title={entry.timestamp}>
          {timeAgo(entry.timestamp)}
        </span>
      </button>

      {/* Expanded detail */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
            className="overflow-hidden"
          >
            <ActivityDetail entry={entry} bqStats={{ totalBQDuration, totalBQBytes, totalBQCost }} />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ─── Activity Detail ────────────────────────────────────────────────────────

function ActivityDetail({
  entry,
  bqStats,
}: {
  entry: ActivityEntry;
  bqStats: { totalBQDuration: number; totalBQBytes: number; totalBQCost: number };
}) {
  const [copiedCurl, setCopiedCurl] = useState(false);
  const [copiedSql, setCopiedSql] = useState<string | null>(null);

  const handleCopyCurl = useCallback(() => {
    navigator.clipboard.writeText(generateCurl(entry));
    setCopiedCurl(true);
    setTimeout(() => setCopiedCurl(false), 2000);
  }, [entry]);

  const handleCopySql = useCallback((sql: string, id: string) => {
    navigator.clipboard.writeText(sql);
    setCopiedSql(id);
    setTimeout(() => setCopiedSql(null), 2000);
  }, []);

  const hasBQ = entry.bigquery && entry.bigquery.length > 0;

  return (
    <div className="border-t border-slate-100 bg-slate-50/50 px-4 py-3 space-y-3">
      {/* Meta row */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {new Date(entry.timestamp).toLocaleString()}
        </span>
        <span className="flex items-center gap-1">
          <User className="w-3 h-3" />
          {entry.user ?? 'Unattributed'}
        </span>
        <span className="flex items-center gap-1">
          <Zap className="w-3 h-3" />
          {formatDuration(entry.durationMs)} total
        </span>
        {entry.responseSize != null && (
          <span>Response: {formatBytes(entry.responseSize)}</span>
        )}
      </div>

      {/* cURL */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Reproduce</span>
          <button
            onClick={handleCopyCurl}
            className="flex items-center gap-1 text-[10px] text-slate-500 hover:text-slate-700 transition-colors"
          >
            {copiedCurl ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
            {copiedCurl ? 'Copied' : 'Copy cURL'}
          </button>
        </div>
        <pre className="bg-slate-900 text-slate-200 text-[11px] p-3 rounded-lg overflow-x-auto leading-relaxed">
          {generateCurl(entry)}
        </pre>
      </div>

      {/* Request body */}
      {!!entry.requestBody && (
        <div>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 block mb-1">Request Body</span>
          <pre className="bg-white border border-slate-200 text-[11px] p-3 rounded-lg overflow-x-auto max-h-48 text-slate-700 leading-relaxed">
            {JSON.stringify(entry.requestBody, null, 2)}
          </pre>
        </div>
      )}

      {/* BigQuery queries */}
      {hasBQ && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              BigQuery Queries ({entry.bigquery!.length})
            </span>
            <span className="text-[10px] text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded font-medium">
              {formatBytes(bqStats.totalBQBytes)} scanned
            </span>
            <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded font-medium">
              {formatCost(bqStats.totalBQCost)}
            </span>
          </div>

          <div className="space-y-2">
            {entry.bigquery!.map((bq, i) => {
              const queryId = `${entry.id}-${i}`;
              return (
                <div key={i} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                  {/* BQ stats bar */}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 border-b border-slate-100 bg-slate-50/50 text-[11px]">
                    <span className="text-slate-600">
                      <Clock className="w-3 h-3 inline mr-0.5" />
                      {formatDuration(bq.durationMs)}
                    </span>
                    <span className="text-slate-600">
                      Processed: <strong>{formatBytes(bq.totalBytesProcessed)}</strong>
                    </span>
                    <span className="text-slate-600">
                      Billed: <strong>{formatBytes(bq.totalBytesBilled)}</strong>
                    </span>
                    <span className={cn(
                      'px-1.5 py-0.5 rounded text-[10px] font-medium',
                      bq.cacheHit ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500',
                    )}>
                      {bq.cacheHit ? 'Cache hit' : 'Full scan'}
                    </span>
                    <span className="text-amber-600 font-medium">
                      {formatCost(bq.costUsd)}
                    </span>
                    <button
                      onClick={() => handleCopySql(bq.query, queryId)}
                      className="ml-auto flex items-center gap-1 text-[10px] text-slate-500 hover:text-slate-700 transition-colors"
                    >
                      {copiedSql === queryId ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                      {copiedSql === queryId ? 'Copied' : 'Copy SQL'}
                    </button>
                  </div>
                  {/* SQL */}
                  <pre className="text-[11px] p-3 overflow-x-auto max-h-60 text-slate-700 leading-relaxed">
                    {bq.query}
                  </pre>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
